// ============================================================================
// Montos, fechas, depreciación en línea recta y garantía
// ============================================================================
// La depreciación es INFORMATIVA (D4): no genera gastos ni toca presupuestos,
// para no contar dos veces la compra. El valor en libros nunca se guarda: se
// calcula al leer, con la fecha que se pida.
//
// Criterio mensual: la depreciación empieza el mes SIGUIENTE a la fecha de
// compra y solo cuentan meses completos. Se deprecia (costo − rescate) en
// `usefulLifeMonths` cuotas iguales y nunca baja del valor de rescate. La baja
// (`retiredAt`) la detiene.
// ============================================================================

import type { Asset } from "./types";
import { BASE_CURRENCY, ASSETS_TIMEZONE } from "./vocab";
import type { AssetCategory } from "./vocab";

/**
 * Vida útil por omisión, en meses. Son valores operativos para el valor en
 * libros informativo, no tasas fiscales. ⚠️ Pendiente de que Finanzas los
 * valide; cada compañía los cambia en `asset_settings.usefulLifeMonths`.
 */
export const DEFAULT_USEFUL_LIFE_MONTHS: Record<AssetCategory, number> = {
  computo: 36,
  telefonia: 24,
  mobiliario: 120,
  herramienta: 60,
  maquinaria: 120,
  equipo_oficina: 60,
  otro: 60,
};
export const USEFUL_LIFE_PENDING_VALIDATION = true;

/** Límite de vida útil que acepta la captura (50 años). */
export const MAX_USEFUL_LIFE_MONTHS = 600;

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/** `YYYY-MM-DD` que existe en el calendario (acepta ISO completo y toma la fecha). */
export function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const m = ISO_DATE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

/** Fecha `YYYY-MM-DD` de un instante en la zona de la plataforma (por omisión, CDMX). */
export function isoDateInTimeZone(at: Date, timeZone: string = ASSETS_TIMEZONE): string {
  // en-CA formatea como YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(at);
}

/**
 * Monto en MXN. MXN no necesita tipo de cambio; otra moneda sin tipo de cambio
 * válido devuelve null. Sin monto, null.
 */
export function toBaseAmount(
  amount: number | null | undefined,
  currency: string | null | undefined,
  exchangeRate: number | null | undefined,
): number | null {
  if (amount == null || !Number.isFinite(Number(amount))) return null;
  const cur = String(currency || BASE_CURRENCY).toUpperCase();
  if (cur === BASE_CURRENCY) return round2(Number(amount));
  const rate = Number(exchangeRate);
  return Number.isFinite(rate) && rate > 0 ? round2(Number(amount) * rate) : null;
}

/** Índice absoluto de mes (año*12 + mes-1) de una fecha ISO. */
function monthIndex(iso: string): number | null {
  const m = ISO_DATE.exec(iso);
  if (!m) return null;
  return Number(m[1]) * 12 + (Number(m[2]) - 1);
}

/** Vida útil efectiva: la del activo, la de la compañía o la de la categoría. */
export function effectiveUsefulLifeMonths(
  asset: Pick<Asset, "category" | "depreciation">,
  byCategory: Partial<Record<AssetCategory, number>> = {},
): number {
  const own = asset.depreciation?.usefulLifeMonths;
  if (own != null && Number.isInteger(own) && own > 0) return own;
  const company = byCategory[asset.category];
  if (company != null && Number.isInteger(company) && company > 0) return company;
  return DEFAULT_USEFUL_LIFE_MONTHS[asset.category] ?? DEFAULT_USEFUL_LIFE_MONTHS.otro;
}

export interface BookValue {
  /** Costo en MXN. */
  cost: number;
  salvage: number;
  usefulLifeMonths: number;
  monthlyAmount: number;
  monthsDepreciated: number;
  remainingMonths: number;
  accumulated: number;
  bookValue: number;
  fullyDepreciated: boolean;
}

/**
 * Depreciación acumulada y valor en libros al `asOf` (`YYYY-MM-DD`). Devuelve
 * null si el activo no tiene costo en MXN (sin costo, sin tipo de cambio, o un
 * usuario sin `feat:activos.costos` al que el serializer ya se lo quitó).
 */
export function computeBookValue(
  asset: Pick<Asset, "category" | "depreciation" | "purchase" | "retiredAt">,
  asOf: string,
  byCategory: Partial<Record<AssetCategory, number>> = {},
): BookValue | null {
  const p = asset.purchase;
  const rawCost = p?.costBase ?? toBaseAmount(p?.unitCost, p?.currency, p?.exchangeRate);
  if (rawCost == null || !Number.isFinite(rawCost)) return null;
  const cost = Math.max(0, rawCost);
  const salvage = Math.min(cost, Math.max(0, Number(asset.depreciation?.salvage) || 0));
  const life = effectiveUsefulLifeMonths(asset, byCategory);
  const depreciable = cost - salvage;

  const start = p?.date ? monthIndex(p.date) : null;
  let end = monthIndex(asOf);
  if (asset.retiredAt) {
    const retired = monthIndex(asset.retiredAt);
    if (retired !== null && end !== null) end = Math.min(end, retired);
  }
  // Meses completos entre el primer día del mes siguiente a la compra y el
  // primer día del mes de corte, sin pasar de la vida útil.
  const elapsed = start === null || end === null ? 0 : Math.max(0, end - (start + 1));
  const months = Math.min(life, elapsed);
  // Se calcula sobre el total para que el redondeo mensual no se acumule.
  const accumulated = months >= life ? round2(depreciable) : round2((depreciable * months) / life);
  return {
    cost: round2(cost),
    salvage: round2(salvage),
    usefulLifeMonths: life,
    monthlyAmount: round2(depreciable / life),
    monthsDepreciated: months,
    remainingMonths: life - months,
    accumulated,
    bookValue: round2(cost - accumulated),
    fullyDepreciated: depreciable > 0 && months >= life,
  };
}

export type WarrantyStatus = "sin_garantia" | "vigente" | "por_vencer" | "vencida";

/** Estado de la garantía al `asOf`. «por_vencer» = vence dentro de `warningDays` días (30). */
export function warrantyStatus(
  warrantyUntil: string | null | undefined,
  asOf: string,
  warningDays = 30,
): { status: WarrantyStatus; daysLeft: number | null } {
  if (!isIsoDate(warrantyUntil) || !isIsoDate(asOf)) return { status: "sin_garantia", daysLeft: null };
  const day = (iso: string) => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
  const daysLeft = Math.round((day(warrantyUntil) - day(asOf)) / 86_400_000);
  if (daysLeft < 0) return { status: "vencida", daysLeft };
  if (daysLeft <= warningDays) return { status: "por_vencer", daysLeft };
  return { status: "vigente", daysLeft };
}
